import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ConfigurationService, DashboardContext, DialogService } from '@sdbn/appclient';
import { TranslationService } from '@sdbn/common';
import { Subject, Subscription } from 'rxjs';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';
import { StationSelection } from 'src/tmc-yard/common/model/station-selection.model';
import { SacClientContext } from '../../model/sac-client.context';
import { AvailableTransport, AvailableTransportsSelection, SacSelection, TransportGroup } from '../../model/sac-client.model';
import { SacClientService } from '../../service/sac-client.service';
import { CreateMovingOrderComponent } from '../create-moving-order/create-moving-order.component';
import { ManagePlannedAssignmentsComponent } from '../manage-planned-assignments/manage-planned-assignments.component';
import { StationType } from 'src/tmc-yard/client/model/station-type.enum';

@Component({
  selector: 'app-sac-transports',
  templateUrl: './sac-transports.component.html',
  styleUrls: ['./sac-transports.component.scss']
})
export class SacTransportComponent implements OnInit, OnDestroy {

  stationType = StationType;

  sacClientContext: SacClientContext;
  refreshSubscription: Subscription;

  refreshTrigger: Subject<void>;
  refreshTriggerSubscription: Subscription;


  transportGroups = new Array<TransportGroup>();

  selectedTransportGroupKey: string;
  selectedTransportId: number;

  listWidth = 350;

  featureMovingOrderActive = false;
  featurePlannedTransportsActive = false;

  selection: SacSelection;

  @Input('selection') set setSelection(value: SacSelection) {
    this.selection = value;
    this.updateTransports();
  }

  @Input() dashboardContext: DashboardContext;

  @Input('refreshTrigger') set setRefreshTrigger(value: Subject<void>) {
    if (this.refreshTriggerSubscription != null) {
      this.refreshTriggerSubscription.unsubscribe();
    }
    this.refreshTrigger = value;
    this.refreshTriggerSubscription = this.refreshTrigger.subscribe(() => {
      this.updateTransports();
    });
  }

  @Input('sacClientContext') set setSacClientContext(sacClientContext: SacClientContext) {
    this.sacClientContext = sacClientContext;
    this.updateTransports();

    if (this.refreshSubscription != null) {
      this.refreshSubscription.unsubscribe();
    }
    this.refreshSubscription = this.tmcContextService.stationUsagesUpdated$.subscribe(
      (key) => {
        if (key === 'end') {
          this.updateTransports();
        }
      }
    );
  }

  constructor(
    public cps: ConfigurationService,
    public translationService: TranslationService,
    private sacClientService: SacClientService,
    private tmcContextService: TmcContextService,
    private dialogService: DialogService
  ) {
    this.transportGroups.push(new TransportGroup({ key: 'transports', nameDictionaryKey: 'tmc-yard.widgets.sac-client.sac-transports.waiting-transports' }));
    this.transportGroups.push(new TransportGroup({ key: 'backupTransports', nameDictionaryKey: 'tmc-yard.widgets.sac-client.sac-transports.backup-transports' }));
  }

  ngOnInit() {

    this.listWidth = +this.cps.getClientParamValue('clientproperty.sac-client.available-transports.column-width', null);
    this.featureMovingOrderActive = this.cps.getClientParamValue('features.moving-order', false) == 'true';
    this.featurePlannedTransportsActive = this.cps.getClientParamValue('features.planned-transports', false) == 'true';

    this.updateTransports();

  }

  ngOnDestroy(): void {
    if (this.refreshSubscription != null) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = null;
    }
    if (this.refreshTriggerSubscription != null) {
      this.refreshTriggerSubscription.unsubscribe();
      this.refreshTriggerSubscription = null;
    }
  }

  private updateTransports() {
    if (this.selection != null) {
      const selection = new AvailableTransportsSelection();
      selection.station = { id: this.selection.station?.id };
      selection.stationSet = { id: this.selection.stationSet?.id };
      this.sacClientService.loadAvailableTransports(selection).subscribe(
        availableTransports => {

          const transportGroup = this.transportGroups.find(tg => tg.key === 'transports');
          const backupTransports = this.transportGroups.find(tg => tg.key === 'backupTransports');

          this.mergeTransportsToGroup(availableTransports.transports, transportGroup);
          this.mergeTransportsToGroup(availableTransports.backupTransports, backupTransports);

          this.setDefaultSelectionOfGroup(transportGroup, backupTransports);

        }
      );
    } else {
      for (const tg of this.transportGroups) {
        tg.transports.splice(0, tg.transports.length);
      }
    }
  }

  public setSelectedTransport(transportGroup: TransportGroup, transport: AvailableTransport) {
    if (transport == null) {
      this.selectedTransportGroupKey = null;
      this.selectedTransportId = null;
      return;
    }

    if (transport.id === this.selectedTransportId) {
      this.selectedTransportGroupKey = null;
      this.selectedTransportId = null;
    } else {
      this.selectedTransportGroupKey = transportGroup.key;
      this.selectedTransportId = transport.id;
    }

    if (this.dashboardContext != null) {
      this.dashboardContext.setContextValue('selected-transport', { id: this.selectedTransportId });
    }
  }

  private mergeTransportsToGroup(transports: AvailableTransport[], transportGroup: TransportGroup) {

    for (let i = 0; i < transports.length; i++) {
      const newTransport = transports[i];
      if (i < transportGroup.transports.length) {
        transportGroup.transports[i].update(newTransport);
      } else {
        transportGroup.transports.push(newTransport);
      }
    }

    if (transports.length < transportGroup.transports.length) {
      transportGroup.transports.splice(transports.length, transportGroup.transports.length);
    }
  }

  private setDefaultSelectionOfGroup(transportGroup: TransportGroup, backupGroup: TransportGroup) {

    // wenn es eine aktuelle Selektion gibt und der in der Liste ist, dann setzen
    let fountTransport = transportGroup.transports.find(t => t.id === this.selectedTransportId);
    let fountBackupTransport = backupGroup.transports.find(t => t.id === this.selectedTransportId);

    if (fountTransport != null || fountBackupTransport != null) {
      return;
    } else {

      const transportForUmbrueckung = this.findTransportForUmbueckungOfStation(transportGroup);
      if (transportForUmbrueckung != null) {
        this.setSelectedTransport(transportGroup, transportForUmbrueckung);
      } else {
        this.setSelectedTransport(transportGroup, transportGroup.transports[0]);
      }
    }
  }

  private findTransportForUmbueckungOfStation(transportGroup: TransportGroup) {

    const selectedStation = this.tmcContextService.findStationById(this.selection.station?.id);

    for (const t of transportGroup.transports) {
      if (t.umbrueckungTargetTransportId != null &&
        selectedStation.stationUsage.transports[0]?.id === t.umbrueckungTargetTransportId) {
        return t;
      }
    }
  }

  managePlannedAssignments() {
    this.dialogService.openLarge(ManagePlannedAssignmentsComponent, this.selection).afterClosed().subscribe(() => {
      this.updateTransports();
    });
  }

  createMovingOrder() {
    this.dialogService.openLarge(CreateMovingOrderComponent, this.selection).afterClosed().subscribe(() => {
      this.updateTransports();
    });
  }
}


